import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Markdown } from '../..';
import { ThemeProps } from '../../../types';

type FontAlign = 'center' | 'left' | 'right';
type FontAlignSelf = 'center' | 'flex-start' | 'flex-end';
type FontColors =
  | 'accent'
  | 'error'
  | 'muted'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'white';
type FontWeights = 'light' | 'medium' | 'bold';
type TextVariants = 'accordionHeader' | 'h1' | 'p' | 'smallPrice';
type TextElement = any;

export interface TextProps {
  /**
   * The alignment of your text. This is a convinience prop that shouldn't be used extensively.
   */
  align?: FontAlign;
  /**
   * The flex alignment of your text. This is a convinience prop that shouldn't be used extensively.
   */
  alignSelf?: FontAlignSelf;
  /**
   * The HTML element to render. Defaults to 'p' element.
   */
  as: TextElement;
  /**
   * The css class for the input. Handled internally by emotion
   */
  className?: string;
  /**
   * The react child to render
   */
  children: ReactNode;
  /**
   * Change the color of text. This is a convinience prop that shouldn't be used extensively
   */
  color?: FontColors;
  /**
   * Extend the default markdown renderer by component with this prop
   */
  customRenderer?: object;
  /**
   * the label prop `for`
   */
  htmlFor?: string;
  /**
   * Theme specific variants of text.
   */
  variant: TextVariants;
  /**
   * parse text with markdown
   */
  withMarkdown?: boolean;
  /**
   * The weight of the font. This is a onvinience prop that shouldn't be used extensively
   */
  weight?: FontWeights;
}

const applyColor = ({ colors }: ThemeProps, color: FontColors) => ({
  color: colors.text[color],
});
const applyVariant = ({ mq, colors }: ThemeProps, variant: TextVariants) => {
  const variantStyles: any = {
    accordionDetail: {
      fontSize: 12,
      [mq.smallMobile]: {
        fontSize: 10,
      },
      [mq.desktop]: {
        fontSize: 24,
      },
    },
    accordionHeader: {
      fontSize: 18,
      lineHeight: 1.25,
      [mq.smallMobile]: {
        fontSize: 16,
      },
      [mq.desktop]: {
        fontSize: 20,
      },
    },
    disclaimer: {
      fontSize: 12,
    },
    h1: {
      fontSize: 28,
    },
    listItem: {
      fontSize: 14,
    },
    p: {
      fontSize: 16,
    },
    price: {
      display: 'inline-block',
      fontSize: 20,
      [mq.smallMobile]: {
        fontSize: 16,
      },
      [mq.desktop]: {
        fontSize: 24,
      },
    },
    priceFrequency: {
      fontSize: 12,
      [mq.smallMobile]: {
        fontSize: 11,
      },
      [mq.desktop]: {
        fontSize: 16,
      },
    },
    smallPrice: {
      [mq.smallMobile]: {
        fontSize: 14,
      },
    },
  };
  return variantStyles[variant];
};
const applyWeight = ({ fonts }: ThemeProps, weight: FontWeights) => ({
  fontWeight: fonts.weights[weight],
});

const Text = ({
  as = 'p',
  children,
  className,
  customRenderer,
  htmlFor,
  withMarkdown,
}: TextProps) => {
  if (withMarkdown) {
    return (
      <Markdown customRenderer={customRenderer} className={className}>
        {children}
      </Markdown>
    );
  }

  const Element: TextElement = as;
  return (
    <Element className={className} htmlFor={htmlFor}>
      {children}
    </Element>
  );
};

const StyledText = styled((props: any) => <Text {...props} />)`
  ${({
    align,
    alignSelf,
    color,
    theme,
    variant,
    weight = 'medium',
  }: {
    align: FontAlign;
    alignSelf: FontAlignSelf;
    color: FontColors;
    theme: ThemeProps;
    variant: TextVariants;
    weight: FontWeights;
  }) => ({
    alignSelf,
    fontFamily: theme.fonts.families,
    textAlign: align || 'initial',
    ...applyColor(theme, color),
    ...applyVariant(theme, variant),
    ...applyWeight(theme, weight),
  })}
`;

export default StyledText;
